import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { useUpdateProfile } from '@/features/profile/hooks/useUpdateProfile';
import { useToast } from '@/shared/hooks/use-toast';
import { useI18n } from '@/contexts/I18nContext';
import { type BeltColor } from '@/constants/belt';
import { OnboardingShell } from './OnboardingShell';
import { markOptionalFieldsCompleted } from './markOptionalFieldsCompleted';
import { OnboardingStep1Profile } from './steps/OnboardingStep1Profile';
import { PractitionerStep3Benefits } from './steps/PractitionerStep3Benefits';
import { ONBOARDING_STEPS, ONBOARDING_TOTAL_STEPS } from './onboardingSteps';

interface PractitionerOnboardingProps {
  username: string;
  onComplete: (destination?: string) => void;
}

const COMPLETE_PROFILE = ONBOARDING_STEPS.COMPLETE_PROFILE;
const BENEFITS = ONBOARDING_STEPS.FINAL;

export const PractitionerOnboarding = ({ username, onComplete }: PractitionerOnboardingProps) => {
  const { t } = useI18n();
  const { toast } = useToast();
  const updateProfile = useUpdateProfile();

  const [step, setStep] = useState(COMPLETE_PROFILE);
  const [name, setName] = useState('');
  const [belt, setBelt] = useState<BeltColor>('white');
  const [degrees, setDegrees] = useState(0);
  const [photoUrl, setPhotoUrl] = useState<string | null>(null);
  const [academyId, setAcademyId] = useState('');
  const [academyName, setAcademyName] = useState('');

  useEffect(() => {
    if (step === BENEFITS) void markOptionalFieldsCompleted(username);
  }, [step, username]);

  const saveProfile = async () => {
    try {
      await updateProfile.mutateAsync({
        full_name: name,
        belt_rank: belt,
        stripe_rank: degrees,
        ...(photoUrl ? { profile_picture_url: photoUrl } : {}),
        ...(academyName ? { jiu_jitsu_school: academyName } : {}),
        ...(academyId ? { main_academy_id: academyId } : {}),
      });
      setStep(BENEFITS);
    } catch {
      toast({ title: t('onboarding.practitioner.save_error_title'), description: t('onboarding.practitioner.save_error_description'), variant: 'destructive' });
    }
  };

  const handleComplete = () => onComplete('/calendar');

  const stickyFooter = step === COMPLETE_PROFILE ? (
    <Button
      variant="primary"
      size="lg"
      disabled={name.trim().length === 0}
      onClick={saveProfile}
      className="w-full"
    >
      {t('onboarding.continue')}
    </Button>
  ) : (
    <div className="flex gap-3">
      <Button variant="outline" size="lg" onClick={() => setStep(COMPLETE_PROFILE)} className="flex-shrink-0 px-6">
        {t('common.back')}
      </Button>
      <Button variant="primary" size="lg" onClick={handleComplete} className="flex-1">
        {t('onboarding.practitioner.first_training')}
      </Button>
    </div>
  );

  return (
    <OnboardingShell
      step={step}
      totalSteps={ONBOARDING_TOTAL_STEPS}
      title={step === COMPLETE_PROFILE ? t('onboarding.complete_profile_title') : undefined}
      subtitle={step === COMPLETE_PROFILE ? t('onboarding.complete_profile_subtitle') : undefined}
      stickyFooter={stickyFooter}
    >
      {step === COMPLETE_PROFILE && (
        <OnboardingStep1Profile
          username={username}
          name={name}
          belt={belt}
          degrees={degrees}
          photoUrl={photoUrl}
          academyId={academyId}
          academyName={academyName}
          onNameChange={setName}
          onBeltChange={setBelt}
          onDegreesChange={setDegrees}
          onPhotoChange={setPhotoUrl}
          onAcademyChange={(id, n) => { setAcademyId(id); setAcademyName(n); }}
        />
      )}
      {step === BENEFITS && <PractitionerStep3Benefits />}
    </OnboardingShell>
  );
};
